import React, { useState } from "react"
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMapEvents,
} from "react-leaflet"
import { VesselInfo } from "../hooks/useFetchVesselInfo"
import { TrackVesselStruct } from "../hooks/useTrackVessel"
import { getBGPill } from "../helpers/helper"
import {
  calculateBearing,
  splitTrailByDateline,
  getShipIcon,
  FlyToSelectedVessel,
} from "../helpers/helper"
import L from "leaflet"
import { getEnv } from "../../../config"
import { VesselPosition } from "modules/Prevetting/hooks/usePrevetAPI"
import { VesselStay } from "modules/Prevetting/hooks/useFetchVesselStays"
import { fixAntimeridian } from "modules/MapComponent/components/map/positions"
import moment from "moment"

export interface VesselTrackingMapProps {
  vesselInfos: VesselInfo[] | undefined
  fetchVesselInfo: (listID: string[]) => Promise<void>
  isGetVesselInfoLoading: boolean
  trackVesselDetails: TrackVesselStruct[] | undefined
  trackVessel: (imos: string[]) => Promise<void>
  isTrackVesselLoading: boolean
  clearTrackVesselDetails: () => void
  selectedVesselPosition: [number, number] | null
  routeViewImo: string | null
  onVesselClickForRoute: (imo: string) => void
  onClearRouteView: () => void
  statusFilter: string
  loadingFilter: string
  vesselStays: VesselStay[]
  isRouteLoading: boolean
  vesselPositions: VesselPosition[] | []
}

const MAPTILER_KEY = getEnv("REACT_APP_MAPTILER_KEY")
const HYBRID_MAP_URL = `https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`

function getPortStayColor(duration: number): string {
  if (duration >= 20) return "#ec4561"
  if (duration >= 10) return "#ffdd15"
  return "#02a499"
}

function getPortStayDurationText(days: number) {
  return days < 10 ? "Short port stay" : "Prolonged port stay"
}

function getPortStayColorDot(days: number) {
  return (
    <div
      style={{
        borderRadius: "100%",
        height: "10px",
        width: "10px",
        backgroundColor: getPortStayColor(days),
        display: "inline-block",
      }}
    />
  )
}

function getPortStayActivity(activity: string) {
  if (activity === "L") return "Loading"
  if (activity === "D") return "Discharging"
  if (activity === "F") return "Bunkering"
  return activity
}

function getSpeedColor(speed: number): string {
  if (speed >= 12) return "#02a499"
  if (speed >= 9) return "#ffdd15"
  return "#ec4561"
}

function ZoomTracker({ onZoomChange }: { onZoomChange: (z: number) => void }) {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  })
  return null
}

const NAME_TAG_ZOOM_THRESHOLD = 5
const NAME_TAG_VESSEL_THRESHOLD = 100

function getLadenStatus(pos: VesselPosition): "laden" | "ballast" {
  if (pos.model_laden === true) return "laden"
  if (pos.model_laden === false) return "ballast"
  if (pos.model_laden === null && pos.draft_laden === true) return "laden"
  if (pos.model_laden === null && pos.draft_laden === false) return "ballast"
  return pos.draft_ratio > 0.7 ? "laden" : "ballast"
}

function VesselTrackingMap({
  vesselInfos,
  isGetVesselInfoLoading,
  trackVesselDetails,
  isTrackVesselLoading,
  selectedVesselPosition,
  routeViewImo,
  onVesselClickForRoute,
  onClearRouteView,
  statusFilter,
  loadingFilter,
  vesselStays,
  isRouteLoading,
  vesselPositions,
}: VesselTrackingMapProps) {
  const [hoveredImo, setHoveredImo] = useState<string | null>(null)
  const [zoom, setZoom] = useState<number>(3)

  const visibleVesselCount =
    trackVesselDetails
      ?.filter((track) => track.vesselPosition !== null)
      .filter((vessel) => {
        const matchesStatus =
          statusFilter === "all" || vessel.status === statusFilter
        const matchesBL =
          loadingFilter === "all" || vessel.loading === loadingFilter
        return matchesStatus && matchesBL
      }).length ?? 0

  const showNameTags =
    visibleVesselCount <= NAME_TAG_VESSEL_THRESHOLD ||
    zoom >= NAME_TAG_ZOOM_THRESHOLD

  const routeVessel = routeViewImo
    ? trackVesselDetails?.find((v) => v.imo === routeViewImo)
    : null

  const routePositions: VesselPosition[] = (() => {
    if (!vesselPositions?.length) return []
    const recent = routeVessel?.vesselPosition ?? []
    // Dedupe by pos_date; recent positions take precedence over historical
    const byDate = new Map<string, VesselPosition>()
    for (const p of vesselPositions) byDate.set(p.pos_date, p)
    for (const p of recent) byDate.set(p.pos_date, p)
    const merged = Array.from(byDate.values()).sort(
      (a, b) => new Date(a.pos_date).getTime() - new Date(b.pos_date).getTime()
    )
    return fixAntimeridian(merged.map((p) => ({ ...p })))
  })()

  return (
    <div
      className="VesselTrackingContainer_map"
      style={{ position: "relative" }}
    >
      {routeViewImo && (
        <button
          onClick={onClearRouteView}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1000,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            background: "white",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: 18,
            lineHeight: "1",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
      )}
      {(isTrackVesselLoading || isGetVesselInfoLoading) && (
        <div className="VesselTrackingSpinner-Overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {isRouteLoading && routeViewImo && (
        <div className="VesselTrackingSpinner-Overlay">
          <div className="d-flex flex-column align-items-center gap-2">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span style={{ fontWeight: 600, fontSize: 14, color: "white" }}>
              Loading 1 year trade route...
            </span>
          </div>
        </div>
      )}
      <MapContainer
        center={[20, 75]}
        zoom={3}
        minZoom={3}
        maxZoom={10}
        scrollWheelZoom={true}
        style={{ height: "80vh" }}
        worldCopyJump={false}
        maxBoundsViscosity={0.0}
        /* maxBounds={[
          [85, -180],
          [-85, 180],
        ]} */
      >
        <TileLayer
          url={HYBRID_MAP_URL}
          attribution='&copy; <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noreferrer">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>'
        />
        {selectedVesselPosition && (
          <FlyToSelectedVessel position={selectedVesselPosition} />
        )}
        <ZoomTracker onZoomChange={setZoom} />

        {/* Vessel markers — dimmed when another vessel's route is active */}
        {vesselInfos?.length !== 0 &&
          trackVesselDetails
            ?.filter((track) => track.vesselPosition !== null)
            .filter((vessel) => {
              const matchesStatus =
                statusFilter === "all" || vessel.status === statusFilter
              const matchesBL =
                loadingFilter === "all" || vessel.loading === loadingFilter
              return matchesStatus && matchesBL
            })
            .map((vessel) => {
              const dimmed =
                routeViewImo !== null && routeViewImo !== vessel.imo

              /* const trailPositions = [...vessel.vesselPosition].sort(
                (a, b) =>
                  new Date(b.pos_date).getTime() -
                  new Date(a.pos_date).getTime()
              ) */
              const sortedPositions = [...vessel.vesselPosition].sort(
                (a, b) =>
                  new Date(a.pos_date).getTime() -
                  new Date(b.pos_date).getTime()
              )
              const segments = splitTrailByDateline(sortedPositions)
              const latest = sortedPositions[sortedPositions.length - 1]
              const previous =
                sortedPositions[sortedPositions.length - 2] ?? latest
              if (!latest) return null

              const color = getBGPill(latest.sog)
              const speed = latest.sog?.toFixed(1) ?? "?"
              const draft = latest.draft_ratio

              const nameTag = L.divIcon({
                className: "",
                html: `
                  <div style="
                    position: relative;
                    background-color: var(--bs-${color});
                    color: white;
                    padding: 6px 10px;
                    border-radius: 12px;
                    font-size: 12px;
                    white-space: nowrap;
                    display: inline-block;
                    max-width: 200px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                    opacity: ${dimmed ? 0.15 : 1};
                    cursor: pointer;
                  ">
                    ${vessel.name}: ${speed}
                    <div style="
                      content: '';
                      position: absolute;
                      bottom: -6px;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 0;
                      height: 0;
                      border-left: 6px solid transparent;
                      border-right: 6px solid transparent;
                      border-top: 6px solid var(--bs-${color});
                    "></div>
                  </div>
                `,
                iconAnchor: [65, 45],
              })

              /* const next = trailPositions[1] || latest */
              const heading = calculateBearing(
                previous.lat,
                previous.lon,
                latest.lat,
                latest.lon
              )

              return (
                <React.Fragment key={vessel.imo}>
                  {segments.map((segment, idx) => (
                    <Polyline
                      key={`${vessel.imo}-segment-${idx}`}
                      positions={segment}
                      pathOptions={{
                        color:
                          hoveredImo === vessel.imo.toString()
                            ? `var(--bs-${color})`
                            : "#FFFFFF",
                        weight: 1,
                        opacity: dimmed ? 0.1 : 1,
                      }}
                    />
                  ))}

                  <Marker
                    position={[latest.lat, latest.lon]}
                    icon={getShipIcon(heading, dimmed)}
                    rotationAngle={heading}
                    rotationOrigin="center"
                    eventHandlers={
                      dimmed
                        ? {}
                        : { click: () => onVesselClickForRoute(vessel.imo) }
                    }
                  />

                  {!dimmed && showNameTags && (
                    <Marker
                      position={[latest.lat, latest.lon]}
                      icon={nameTag}
                      eventHandlers={{
                        mouseover: () => setHoveredImo(vessel.imo),
                        mouseout: () => setHoveredImo(null),
                        click: () => onVesselClickForRoute(vessel.imo),
                      }}
                    >
                      <Tooltip
                        direction="top"
                        offset={[0, -10]}
                        opacity={1}
                        permanent={false}
                        sticky
                      >
                        <div style={{ fontSize: "12px" }}>
                          <strong>Vessel Name:</strong> {vessel.name} (
                          {vessel.imo})
                          <br />
                          <strong>Master Destination:</strong>{" "}
                          {latest.master_destination}
                          <br />
                          <strong>ETA:</strong> {latest.master_eta}
                          <br />
                          <strong>Speed:</strong> {speed} kn
                          {vessel.draft && (
                            <>
                              <br />
                              <strong>Draft:</strong>{" "}
                              {vessel.loading === "Ballast"
                                ? `${(vessel.draft * draft).toFixed(2)}M`
                                : `${(vessel.draft * draft).toFixed(2)}M (${(
                                    draft * 100
                                  ).toFixed(2)}% loaded)`}
                            </>
                          )}
                          <br />
                          <strong>B/L:</strong> {vessel.loading}
                          <br />
                          <strong>Status:</strong> {vessel.status}
                        </div>
                      </Tooltip>
                    </Marker>
                  )}
                </React.Fragment>
              )
            })}

        {/* Route view: full year of positions for the selected vessel */}
        {routeViewImo && routePositions.length > 0 && (
          <>
            {routePositions.slice(0, -1).map((pos, i) => (
              <Polyline
                key={`route-line-${pos.pos_date}-${i}`}
                positions={[
                  [pos.lat, pos.lon],
                  [routePositions[i + 1].lat, routePositions[i + 1].lon],
                ]}
                pathOptions={{
                  color: getSpeedColor(pos.sog),
                  weight: 2,
                  dashArray: "4 4 8",
                }}
              />
            ))}
            {routePositions.map((pos) => {
              const laden = getLadenStatus(pos)
              const markerColor = laden === "laden" ? "#3050A2" : "#FF5B29"
              return (
                <CircleMarker
                  key={`route-dot-${pos.pos_date}`}
                  center={[pos.lat, pos.lon]}
                  radius={4}
                  stroke={false}
                  fillOpacity={0.6}
                  fillColor={markerColor}
                  eventHandlers={{
                    mouseover: (e: any) => e.target.openPopup(),
                    mouseout: (e: any) => e.target.closePopup(),
                  }}
                >
                  <Popup>
                    <div style={{ fontSize: "12px" }}>
                      <strong>
                        {moment(pos.pos_date).format("DD-MM-YYYY")}
                      </strong>
                      <br />
                      Speed: {pos.sog?.toFixed(1)} kn
                      <br />
                      <span style={{ color: markerColor, fontWeight: "bold" }}>
                        {laden.toUpperCase()}
                      </span>
                      <br />
                      Destination: {pos.master_destination}
                      <br />
                      Draft Ratio: {pos.draft_ratio}
                      <br />
                      ETA: {pos.master_eta}
                    </div>
                  </Popup>
                </CircleMarker>
              )
            })}
            {/* Port stay markers */}
            {vesselStays.map((stay) => {
              const color = getPortStayColor(stay.duration)
              return (
                <CircleMarker
                  key={`stay-${stay.port}-${stay.date_from}`}
                  center={[stay.lat, stay.lon]}
                  radius={10}
                  stroke={false}
                  fillOpacity={0.9}
                  fillColor={color}
                  eventHandlers={{
                    mouseover: (e: any) => e.target.openPopup(),
                    mouseout: (e: any) => e.target.closePopup(),
                  }}
                >
                  <Popup>
                    <div>Arrival: {stay.date_from}</div>
                    <div>Departure: {stay.date_to}</div>
                    <div className="d-flex align-items-center">
                      <span>Duration :</span>
                      <span className="ms-1 me-1">
                        {getPortStayColorDot(stay.duration)}
                      </span>
                      <span>{stay.duration} Days</span>
                      <span className="ms-1">(</span>
                      <span>{getPortStayDurationText(stay.duration)}</span>
                      <span>)</span>
                    </div>
                    <div>Port : {stay.port}</div>
                    <div>Activity : {getPortStayActivity(stay.activity)}</div>
                  </Popup>
                </CircleMarker>
              )
            })}
          </>
        )}
      </MapContainer>
    </div>
  )
}

export default VesselTrackingMap
